import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFlows } from "@frigade/react";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { Guide, GuideUserStatus } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import { updateGuideAssignees } from "@app/scene/onboarding/store/actions";
import frigadeService from "@app/util/frigade/frigadeService";

import GuideTypeTag from "../../GuideTypeTag";
import AssignModal from "../AssignModal";
import CompletionDrawer from "../CompletionDrawer";

import GuideAssignees from "./GuideAssignees";
import styles from "./GuideCard.module.scss";

const messages = defineMessages({
  assign: {
    id: "onboarding.guides.card.button.assign",
    defaultMessage: "Assign",
    description: "Assign button label on the Guide card",
  },
  assignModalSubTitle: {
    id: "onboarding.guides.card.assignModal.subtitle",
    defaultMessage: "Select teams and users below to assign to the guide",
    description: "Subtitle of the Assign Users modal on the Guides page",
  },
  previewTooltip: {
    id: "onboarding.guides.card.preview.tooltip",
    defaultMessage: "Preview",
    description: "Preview icon tooltip on the Guide card",
  },
});

export interface GuideCardProps {
  className?: string;
  guide: Guide;
  guideStatus?: GuideUserStatus[];
  onToggle?: (guideId: Guide["id"], checked: boolean) => void;
  selected?: boolean;
}

interface Props extends GuideCardProps {
  onUpdateAssignees: typeof updateGuideAssignees.request;
}

const GuideCard: FC<Props> = ({
  className,
  guide,
  guideStatus,
  onToggle,
  onUpdateAssignees,
  selected,
}) => {
  const intl = useIntl();

  const { markFlowNotStarted } = useFlows();

  const handleSelectionChange = useCallback(
    (checked: boolean) => onToggle?.(guide.id, checked),
    [guide.id, onToggle]
  );

  const [assignModalVisible, showAssignModal, hideAssignModal] = useBoolean();
  const handleChangeAssignees = useCallback(
    (userIds: User["id"][]) => {
      hideAssignModal();
      onUpdateAssignees({ guideId: guide.id, userIds });
    },
    [guide.id, hideAssignModal, onUpdateAssignees]
  );

  const handleEyeClick = useCallback(() => {
    if (guide.frigadeFlowId) {
      frigadeService.showFlow(guide.frigadeFlowId);
      markFlowNotStarted(guide.frigadeFlowId);
    }
  }, [guide, markFlowNotStarted]);

  const [completionDrawerVisible, showCompletionDrawer, hideCompletionDrawer] = useBoolean();

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.header}>
        <div className={styles.title}>
          <Checkbox checked={selected} onChange={handleSelectionChange}>
            {guide.displayName}
          </Checkbox>
        </div>
        <Tooltip title={intl.formatMessage(messages.previewTooltip)}>
          <FontAwesomeIcon className={styles.eye} icon={faEye} onClick={handleEyeClick} />
        </Tooltip>
      </div>
      <div className={styles.info}>
        <GuideTypeTag type={guide.type} />
        <GuideAssignees guideStatus={guideStatus} onClick={showCompletionDrawer} />
      </div>
      <div className={styles.description}>{guide.description}</div>
      <div className={styles.buttons}>
        <Button onClick={showAssignModal} type="filled">
          {intl.formatMessage(messages.assign)}
        </Button>
      </div>
      {assignModalVisible && (
        <AssignModal
          allowEmpty
          onChange={handleChangeAssignees}
          onHide={hideAssignModal}
          subTitle={intl.formatMessage(messages.assignModalSubTitle)}
          value={guideStatus?.map(({ user }) => user.id)}
        />
      )}
      {completionDrawerVisible && (
        <CompletionDrawer guide={guide} guideStatus={guideStatus} onHide={hideCompletionDrawer} />
      )}
    </div>
  );
};

const mapDispatchToProps = {
  onUpdateAssignees: updateGuideAssignees.request,
};

export default connect(null, mapDispatchToProps)(GuideCard);
